import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { GAMES_API, type Games } from '@chess/api-games';
import { LOG_API, type Log } from '@chess/api-log';
import { TOAST_API, type Toast } from '@chess/api-toast';
import { SCAN_API, estimate, type ScanService } from '@chess/api-scan';
import { useScanProgress } from '@chess/api-scan/react';
import { SYNC_API, localDay, syncDue, type SyncService, type SyncState } from '@chess/api-sync';
import { ARCHIVES_FOLDER, canImport, statusText, useImport, type Deps } from './store';
import { monthLabel } from './import.ts';
import { ImportButton } from './ImportButton.tsx';
import './library-import-chesscom.css';

// The chess.com importer: one button, "Sync", in the top bar of every screen
// on the rail, with its status line beside it. Each click fetches the months that
// hold the last 30 days and adds their games played in that window (older
// games are never imported), then asks the scan queue for a pass, so every
// game of the window not scanned yet is scanned, even when nothing new came
// in. The same button shows both: importing, then scanning. Nothing is
// enabled until Settings has a username.
//
// The same import is the daily sync (`sync`, `@chess/api-sync`), provided
// here over the import store: due while no import completed today (the
// local day, `lastSyncDay` in the bookkeeping) and none was skipped this
// session; run by the sync screen (plugin screen-sync) before any screen on
// the rail shows. A click on the button is a sync too, so a manual one
// counts for the day. `reset` starts the account over (the landing page's
// way in): the account's games go, and their scans with them, and so does
// the bookkeeping under `archives/chesscom/<account>/`.
export function setup(app: PiletApi) {
  const storage = app.getApi<Storage>(STORAGE_API);
  const settings = storage.then((s) => s.scope('settings'));
  const archives = storage.then((s) => s.scope(ARCHIVES_FOLDER));
  // Every chess.com call is logged with the reason the cache did not serve it,
  // through the app's log API rather than the console.
  const callLog = app.getApi<Log>(LOG_API).then((log) => log.child(app.meta.name));
  const deps: Promise<Deps> = Promise.all([app.getApi<Games>(GAMES_API), archives, callLog, app.getApi<Toast>(TOAST_API)]).then(
    ([games, s, log, toast]) => ({
      games,
      storage: s,
      log: (endpoint, reason) => log.debug(`GET ${endpoint}`, { endpoint, reason }),
      toast,
    }),
  );

  // The daily sync over the import store. `due` reads the bookkeeping again
  // (`load`), so a reload, or a change of account, is seen at once.
  let skippedDay: string | undefined;
  const sync: SyncService = {
    async due() {
      const [s, a] = await Promise.all([settings, archives]);
      await useImport.getState().load(s, a);
      const { username, lastSyncDay } = useImport.getState();
      return syncDue({ username, lastSyncDay, skippedDay }, new Date());
    },
    async run() {
      const d = await deps;
      await useImport.getState().importRecent(d, { auto: true });
      const scan = await app.getApi<ScanService>(SCAN_API);
      await scan.scanAll();
    },
    skip() {
      skippedDay = localDay(new Date());
    },
    async reset() {
      const [d, s, a] = await Promise.all([deps, settings, archives]);
      await useImport.getState().load(s, a);
      const { username } = useImport.getState();
      if (!username) return;
      (await app.getApi<ScanService>(SCAN_API)).stop();
      for (const game of await d.games.list({ owner: username })) await d.games.remove(game.id);
      await a.remove(username);
      skippedDay = undefined;
      useImport.setState({ status: 'idle', progress: undefined, error: undefined, note: undefined, imported: [], archives: undefined, lastSyncDay: undefined });
    },
    state(): SyncState {
      const s = useImport.getState();
      const phase = s.status === 'importing' ? 'importing' : s.status === 'done' ? 'done' : s.status === 'error' ? 'error' : 'idle';
      return {
        phase,
        first: s.lastSyncDay === undefined,
        month: s.status === 'importing' && s.progress ? monthLabel(`${s.progress.month}`) : undefined,
        imported: s.progress?.imported,
        skipped: s.progress?.skipped,
        note: s.note,
        error: s.error,
      };
    },
    subscribe: (listener) => useImport.subscribe(listener),
  };
  app.registerApi(SYNC_API, sync);

  const useLoaded = () => {
    const load = useImport((s) => s.load);
    React.useEffect(() => {
      Promise.all([settings, archives]).then(([s, a]) => load(s, a));
    }, []);
  };

  /** The importer's control, read from the import store and the scan. */
  const Control: React.FC = () => {
    useLoaded();
    const state = useImport();
    const [scan, setScan] = React.useState<ScanService | undefined>(() => app.tryGetApi<ScanService>(SCAN_API));
    React.useEffect(() => {
      if (!scan) void app.getApi<ScanService>(SCAN_API).then(setScan);
    }, [scan]);
    const progress = useScanProgress(scan);
    const importing = state.status === 'importing';
    const scanning = !importing && !!progress?.running;
    const left = progress ? Math.max(0, progress.total - progress.done) : 0;
    return (
      <ImportButton
        phase={importing ? 'importing' : scanning ? 'scanning' : 'idle'}
        status={statusText(state)}
        error={state.status === 'error'}
        enabled={canImport(state)}
        imported={state.progress?.imported}
        scanned={progress?.done}
        total={progress?.total}
        estimate={scan && scanning ? estimate(left, scan.secondsPerGame()) : undefined}
        // The import's new games are scanned on their own (shared-scan); a
        // pass afterwards also scans games left unscanned (a failed scan,
        // nothing new to import).
        onImport={() => void deps.then((d) => state.importRecent(d)).then(() => scan?.scanAll())}
      />
    );
  };
  // Every screen's top bar, on the right (`shared.headerActions`).
  app.registerExtension('shared.headerActions', Control, { order: 10 });
}
